// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import type { TyLangValue } from "@/types";
import { catalogUrl } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import ColorInput from "@/UI/inputs/ColorInput";
import ImagePickerInput from "@/UI/inputs/ImagePickerInput";
import MultiLangInput from "@/UI/inputs/MultiLangInput";
import MultiSelectInput from "@/UI/inputs/MultiSelectInput";
import TextInput from "@/UI/inputs/TextInput";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type TyOrganization = {
    _id: string;
    name: TyLangValue[];
    slug: string;
    logo: string | null;
    cover: string | null;
    color: string | null;
    theme: string;
    languages: string[];
    about: TyLangValue[];
    phone: string | null;
    whatsapp: string | null;
    email: string | null;
    city: string | null;
    address: string | null;
    crNumber: string | null;
};

/**
 * The company's own settings — and the only screen in the app whose output is **public**.
 *
 * Everything here lands on the catalog at `{slug}.lozicore.com`, so the screen says so at the top
 * and links to it. A person editing a project is changing a record; a person editing this is
 * changing a website, and those two deserve to feel different.
 *
 * The screen fetches and the form below it holds the editing, because a form seeded from a response
 * has to start life already filled in — mounting it empty and filling it from an effect is a render
 * the person can see, and a cascade the linter is right to refuse.
 */
export default function SettingsScreen() {
    const t = useTranslation();
    const settings = useFetch<{ organization: TyOrganization; themes: string[] }>("/settings");
    const organization = settings.data?.organization;

    return (
        <section className="settings-screen">
            {settings.loading && <p className="settings-screen__state">{t.common.loading}</p>}
            {settings.error && <p className="settings-screen__state settings-screen__state--error">{settings.error}</p>}

            {organization && (
                <>
                    {/* The screen's whole point, said once at the top: this is the public page. */}
                    <header className="settings-screen__banner">
                        <div>
                            <h2 className="settings-screen__title">{t.settings.catalogTitle}</h2>
                            <p className="settings-screen__hint">{t.settings.catalogHint}</p>
                        </div>

                        <a className="settings-screen__visit" href={catalogUrl(organization.slug)} target="_blank" rel="noreferrer">
                            <span dir="ltr">{organization.slug}</span>
                            <Icon name="eye" size={16} />
                        </a>
                    </header>

                    <SettingsForm organization={organization} />
                </>
            )}
        </section>
    );
}

/**
 * The whole thing is one form with one save. Field-by-field saving reads as helpfulness and behaves
 * as a trap — a half-finished rebrand goes live one keystroke at a time, and there is nothing to
 * abandon when somebody changes their mind.
 *
 * **The logo is picked, not typed.** It comes out of the drive like every other file in the app,
 * uploaded through the tray and stored under the public prefix, so the catalog can show it to an
 * anonymous visitor without a signed URL. Anything this organization has ever uploaded is offered,
 * because a company that re-uploads its logo every time it changes a colour ends up with nine of them.
 */
function SettingsForm({ organization }: { organization: TyOrganization }) {
    const t = useTranslation();

    const save = useFetch<{ organization: TyOrganization }>("/settings", {}, false);
    const patchOrganization = useAuth((state) => state.patchOrganization);

    /**
     * The sidebar, the switcher and the company badge all read the memberships, which are fetched
     * once at sign-in — so a new logo or a renamed company stayed stale on every one of them until
     * the next sign-in. The saved document comes back with the response; this puts it where they
     * read it from.
     */
    useEffect(() => {
        const saved = save.data?.organization;
        if (!saved) return;
        patchOrganization(saved._id, { name: saved.name, logo: saved.logo, color: saved.color });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [save.succeededAt]);

    const [name, setName] = useState<TyLangValue[]>(organization.name ?? []);
    const [languages, setLanguages] = useState<string[]>(organization.languages ?? []);
    const [color, setColor] = useState(organization.color ?? "");
    const [logo, setLogo] = useState<string | null>(organization.logo ?? null);
    const [cover, setCover] = useState<string | null>(organization.cover ?? null);
    const [about, setAbout] = useState<TyLangValue[]>(organization.about ?? []);
    const [phone, setPhone] = useState(organization.phone ?? "");
    const [whatsapp, setWhatsapp] = useState(organization.whatsapp ?? "");
    const [email, setEmail] = useState(organization.email ?? "");
    const [city, setCity] = useState(organization.city ?? "");
    const [address, setAddress] = useState(organization.address ?? "");
    const [crNumber, setCrNumber] = useState(organization.crNumber ?? "");
    const [formError, setFormError] = useState<string | null>(null);

    const handleSave = () => {
        setFormError(null);
        if (!name.some((entry) => entry.value.trim())) return setFormError(t.settings.nameRequired);
        if (!languages.length) return setFormError(t.settings.languagesRequired);
        if (color && !constants.hexColorPattern.test(color)) return setFormError(t.settings.colorInvalid);

        save.invoke({
            method: "PATCH",
            data: {
                name: name.filter((entry) => entry.value.trim()),
                languages,
                color,
                logo,
                cover,
                about: about.filter((entry) => entry.value.trim()),
                phone,
                whatsapp,
                email,
                city,
                address,
                crNumber,
            },
        });
    };

    return (
        <>
            <div className="settings-screen__column">
                <section className="settings-card">
                    <h3 className="settings-card__title">{t.settings.identity}</h3>

                    <MultiLangInput label={t.settings.fields.name} value={name} onChange={setName} />

                    <MultiSelectInput
                        label={t.settings.fields.languages}
                        value={languages}
                        onChange={setLanguages}
                        options={constants.languages.map((code) => ({ value: code, label: constants.languageNames[code] }))}
                    />
                    <p className="settings-card__hint">{t.settings.languagesHint}</p>

                    {/* The paragraph the catalog's company sheet opens with. Without it that sheet is
                        a list of phone numbers, which is not a company. */}
                    <MultiLangInput label={t.settings.fields.about} value={about} onChange={setAbout} multiline />
                    <p className="settings-card__hint">{t.settings.aboutHint}</p>
                </section>

                <section className="settings-card">
                    <h3 className="settings-card__title">{t.settings.brand}</h3>

                    <ColorInput label={t.settings.fields.color} value={color} onChange={setColor} />
                    <p className="settings-card__hint">{t.settings.colorHint}</p>

                    {/* The same picker the gallery and the portrait use. An organization is a
                        public file owner, so what is stored is the permanent url. */}
                    <ImagePickerInput
                        owner={{ model: "Organization", id: organization._id }}
                        slot="logo"
                        max={1}
                        value={logo ? [logo] : []}
                        onChange={(next) => setLogo(next[0] ?? null)}
                        label={t.settings.fields.logo}
                        disabled={save.loading}
                    />

                    <p className="settings-card__hint">{t.settings.logoHint}</p>

                    {/* The band behind the logo on the catalog's company sheet. Optional, and the
                        sheet falls back to the brand colour without one. */}
                    <ImagePickerInput
                        owner={{ model: "Organization", id: organization._id }}
                        slot="gallery"
                        max={1}
                        value={cover ? [cover] : []}
                        onChange={(next) => setCover(next[0] ?? null)}
                        label={t.settings.fields.cover}
                        disabled={save.loading}
                    />

                    <p className="settings-card__hint">{t.settings.coverHint}</p>
                </section>

                <section className="settings-card">
                    <h3 className="settings-card__title">{t.settings.contacts}</h3>
                    <p className="settings-card__hint">{t.settings.contactsHint}</p>

                    <div className="settings-card__pair">
                        <TextInput label={t.settings.fields.phone} value={phone} onChange={setPhone} type="tel" dir="ltr" />
                        <TextInput label={t.settings.fields.whatsapp} value={whatsapp} onChange={setWhatsapp} type="tel" dir="ltr" />
                    </div>

                    <div className="settings-card__pair">
                        <TextInput label={t.settings.fields.email} value={email} onChange={setEmail} type="email" dir="ltr" />
                        <TextInput label={t.settings.fields.city} value={city} onChange={setCity} />
                    </div>

                    <div className="settings-card__pair">
                        <TextInput label={t.settings.fields.crNumber} value={crNumber} onChange={setCrNumber} dir="ltr" />
                        <TextInput label={t.settings.fields.address} value={address} onChange={setAddress} />
                    </div>
                </section>
            </div>

            {/* It follows the form down rather than sitting at the end of it: this page is long on a
                phone, and a save button nobody can find is a page nobody saves. */}
            <footer className="settings-screen__footer">
                {formError && <p className="settings-screen__state settings-screen__state--error">{formError}</p>}
                {save.error && <p className="settings-screen__state settings-screen__state--error">{save.error}</p>}
                {save.succeededAt && !save.error && <p className="settings-screen__state settings-screen__state--done">{t.settings.saved}</p>}

                <button type="button" className="settings-screen__save" onClick={handleSave} disabled={save.loading}>
                    {save.loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </>
    );
}
